/**
 * Clipboard Utilities
 * 
 * Centralized clipboard operations for consistent copy behavior across the app.
 */

/**
 * Copy text to clipboard
 * @param text The text to copy
 * @returns Promise<boolean> - true if successful, false if failed
 */
export async function copyToClipboard(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        console.error('Failed to copy to clipboard:', err);
        return false;
    }
}

/**
 * Copy text to clipboard with a callback for success state
 * Automatically resets the copied state after a timeout
 * @param text The text to copy
 * @param onCopied Callback to set copied state
 * @param resetMs Milliseconds to wait before resetting (default 2000)
 */
export async function copyWithFeedback(
    text: string, 
    onCopied: (copied: boolean) => void,
    resetMs: number = 2000
): Promise<boolean> {
    const success = await copyToClipboard(text);
    if (success) {
        onCopied(true);
        setTimeout(() => onCopied(false), resetMs);
    }
    return success;
}

